import { isDefined } from "@clipboard-health/util-ts";
import { z } from "zod";

const basePayRateStructure = {
  baseRates: z.array(
    z.object({
      baseRate: z.number().min(1, "Base rate must be greater than 0"),
      minYearsOfExperience: z.number().min(0, "Years of experience must be 0 or greater"),
    })
  ),
  differentials: z.object({
    // Reuse the same differential structure as transparency pay rates
    benefitsDifferential: z.number().optional(),
    healthBenefitsDifferential: z.number().optional(),
    ptoBenefitsDifferential: z.number().optional(),
    weekendDifferential: z.number().optional(),
    dayDifferential: z.number().optional(),
    eveningDifferential: z.number().optional(),
    nightDifferential: z.number().optional(),
  }),
};

export const placementPayRateSchema = z.object({
  baseRates: basePayRateStructure.baseRates,
  differentials: basePayRateStructure.differentials.optional(),
});

export type PlacementPayRate = z.infer<typeof placementPayRateSchema>;

export const jobShiftPayRateSchema = z.object({
  baseRates: basePayRateStructure.baseRates.optional(),
  differentials: basePayRateStructure.differentials.optional(),
});

export type JobShiftPayRate = z.infer<typeof jobShiftPayRateSchema>;

const optionalNumberString = z
  .string()
  .transform((value) => (value === "" ? undefined : Number(value)))
  .pipe(z.number().optional());

const payRateFormFields = {
  baseRates: z.array(
    z.object({
      baseRate: z
        .string()
        .transform((value) => (value === "" ? undefined : Number(value)))
        .pipe(z.number().min(1, "Base rate must be greater than 0")),
      minYearsOfExperience: z
        .string()
        .transform((value) => (value === "" ? undefined : Number(value)))
        .pipe(z.number().min(0, "Years of experience must be 0 or greater")),
    })
  ),
  benefitsType: z.enum(["combined", "split"]).default("combined"),
  benefitsDifferential: optionalNumberString.optional(),
  healthBenefitsDifferential: optionalNumberString.optional(),
  ptoBenefitsDifferential: optionalNumberString.optional(),
  weekendDifferential: optionalNumberString.optional(),
  dayDifferential: optionalNumberString.optional(),
  eveningDifferential: optionalNumberString.optional(),
  nightDifferential: optionalNumberString.optional(),
};

export const placementPayRateFormSchema = z.object({
  baseRates: payRateFormFields.baseRates,
  benefitsType: payRateFormFields.benefitsType,
  benefitsDifferential: payRateFormFields.benefitsDifferential,
  healthBenefitsDifferential: payRateFormFields.healthBenefitsDifferential,
  ptoBenefitsDifferential: payRateFormFields.ptoBenefitsDifferential,
  weekendDifferential: payRateFormFields.weekendDifferential,
  dayDifferential: payRateFormFields.dayDifferential,
  eveningDifferential: payRateFormFields.eveningDifferential,
  nightDifferential: payRateFormFields.nightDifferential,
});

export const jobShiftPayRateFormSchema = z.object({
  baseRates: payRateFormFields.baseRates.optional(),
  benefitsType: payRateFormFields.benefitsType.optional(),
  benefitsDifferential: payRateFormFields.benefitsDifferential,
  healthBenefitsDifferential: payRateFormFields.healthBenefitsDifferential,
  ptoBenefitsDifferential: payRateFormFields.ptoBenefitsDifferential,
  weekendDifferential: payRateFormFields.weekendDifferential,
  dayDifferential: payRateFormFields.dayDifferential,
  eveningDifferential: payRateFormFields.eveningDifferential,
  nightDifferential: payRateFormFields.nightDifferential,
});

type PlacementPayRateFormInput = z.input<typeof placementPayRateFormSchema>;
type PlacementPayRateFormOutput = z.infer<typeof placementPayRateFormSchema>;
type JobShiftPayRateFormInput = z.input<typeof jobShiftPayRateFormSchema>;
type JobShiftPayRateFormOutput = z.infer<typeof jobShiftPayRateFormSchema>;

export function mapFormDataToPlacementPayRate(
  formData: PlacementPayRateFormOutput
): PlacementPayRate {
  return {
    baseRates: formData.baseRates,
    differentials: {
      benefitsDifferential:
        formData.benefitsType === "combined" ? formData.benefitsDifferential : undefined,
      healthBenefitsDifferential:
        formData.benefitsType === "split" ? formData.healthBenefitsDifferential : undefined,
      ptoBenefitsDifferential:
        formData.benefitsType === "split" ? formData.ptoBenefitsDifferential : undefined,
      weekendDifferential: formData.weekendDifferential,
      dayDifferential: formData.dayDifferential,
      eveningDifferential: formData.eveningDifferential,
      nightDifferential: formData.nightDifferential,
    },
  };
}

export function mapFormDataToJobShiftPayRate(formData: JobShiftPayRateFormOutput): JobShiftPayRate {
  const hasBenefitsDifferential =
    formData.benefitsType === "split"
      ? isDefined(formData.healthBenefitsDifferential) ||
        isDefined(formData.ptoBenefitsDifferential)
      : isDefined(formData.benefitsDifferential);

  return {
    baseRates: formData.baseRates,
    differentials:
      hasBenefitsDifferential ||
      isDefined(formData.weekendDifferential) ||
      isDefined(formData.dayDifferential) ||
      isDefined(formData.eveningDifferential) ||
      isDefined(formData.nightDifferential)
        ? {
            benefitsDifferential:
              formData.benefitsType === "split" ? undefined : formData.benefitsDifferential,
            healthBenefitsDifferential:
              formData.benefitsType === "split" ? formData.healthBenefitsDifferential : undefined,
            ptoBenefitsDifferential:
              formData.benefitsType === "split" ? formData.ptoBenefitsDifferential : undefined,
            weekendDifferential: formData.weekendDifferential,
            dayDifferential: formData.dayDifferential,
            eveningDifferential: formData.eveningDifferential,
            nightDifferential: formData.nightDifferential,
          }
        : undefined,
  };
}

export function mapPlacementPayRateToFormData(
  payRate?: PlacementPayRate
): PlacementPayRateFormInput {
  if (!payRate) {
    return {
      baseRates: [],
      benefitsType: "combined",
      benefitsDifferential: "",
      healthBenefitsDifferential: "",
      ptoBenefitsDifferential: "",
      weekendDifferential: "",
      dayDifferential: "",
      eveningDifferential: "",
      nightDifferential: "",
    };
  }

  const hasCombinedBenefits = payRate.differentials?.benefitsDifferential !== undefined;
  const hasSplitBenefits =
    payRate.differentials?.healthBenefitsDifferential !== undefined ||
    payRate.differentials?.ptoBenefitsDifferential !== undefined;

  return {
    benefitsType: hasSplitBenefits && !hasCombinedBenefits ? "split" : "combined",
    baseRates: payRate.baseRates.map((baseRate) => ({
      baseRate: baseRate.baseRate.toString(),
      minYearsOfExperience: baseRate.minYearsOfExperience.toString(),
    })),
    benefitsDifferential: payRate.differentials?.benefitsDifferential?.toString(),
    healthBenefitsDifferential: payRate.differentials?.healthBenefitsDifferential?.toString(),
    ptoBenefitsDifferential: payRate.differentials?.ptoBenefitsDifferential?.toString(),
    weekendDifferential: payRate.differentials?.weekendDifferential?.toString(),
    dayDifferential: payRate.differentials?.dayDifferential?.toString(),
    eveningDifferential: payRate.differentials?.eveningDifferential?.toString(),
    nightDifferential: payRate.differentials?.nightDifferential?.toString(),
  };
}

// Helper function to map job shift pay rate to form data
export function mapJobShiftPayRateToFormData(payRate?: JobShiftPayRate): JobShiftPayRateFormInput {
  if (!payRate) {
    return {
      baseRates: undefined,
      benefitsType: undefined,
      benefitsDifferential: undefined,
      healthBenefitsDifferential: undefined,
      ptoBenefitsDifferential: undefined,
      weekendDifferential: undefined,
      dayDifferential: undefined,
      eveningDifferential: undefined,
      nightDifferential: undefined,
    };
  }

  const hasCombinedBenefits = payRate.differentials?.benefitsDifferential !== undefined;
  const hasSplitBenefits =
    payRate.differentials?.healthBenefitsDifferential !== undefined ||
    payRate.differentials?.ptoBenefitsDifferential !== undefined;

  return {
    baseRates: payRate.baseRates?.map((baseRate) => ({
      baseRate: baseRate.baseRate.toString(),
      minYearsOfExperience: baseRate.minYearsOfExperience.toString(),
    })),
    benefitsType: (() => {
      if (!hasCombinedBenefits && !hasSplitBenefits) {
        return undefined;
      }

      if (hasSplitBenefits && !hasCombinedBenefits) {
        return "split";
      }

      return "combined";
    })(),
    benefitsDifferential: payRate.differentials?.benefitsDifferential?.toString(),
    healthBenefitsDifferential: payRate.differentials?.healthBenefitsDifferential?.toString(),
    ptoBenefitsDifferential: payRate.differentials?.ptoBenefitsDifferential?.toString(),
    weekendDifferential: payRate.differentials?.weekendDifferential?.toString(),
    dayDifferential: payRate.differentials?.dayDifferential?.toString(),
    eveningDifferential: payRate.differentials?.eveningDifferential?.toString(),
    nightDifferential: payRate.differentials?.nightDifferential?.toString(),
  };
}
